import { useCallback, useEffect, useRef, useState } from "react";

const KEYBOARD_STEP = 16;
const KEYBOARD_LARGE_STEP = 48;

/** In-memory only — survives sidebar remounts, resets on full page reload. */
const sessionWidths = new Map();

const resolveLimit = (value, fallback) => {
  if (typeof value === "function") return value();
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return fallback;
};

/**
 * Full-viewport drag shield so pointer events are not stolen by iframes
 * (EPUB) or nested viewers while the user is resizing a right-side panel.
 */
const createDragShield = () => {
  const shield = document.createElement("div");
  shield.setAttribute("data-resizable-panel-shield", "");
  shield.setAttribute("aria-hidden", "true");
  Object.assign(shield.style, {
    position: "fixed",
    inset: "0",
    zIndex: "2147483646",
    cursor: "col-resize",
    touchAction: "none",
    userSelect: "none",
    // Transparent but still hit-testable
    background: "transparent",
  });
  document.body.appendChild(shield);
  return shield;
};

const releaseHandleCapture = (handle, pointerId) => {
  if (
    !handle ||
    pointerId == null ||
    typeof handle.releasePointerCapture !== "function"
  ) {
    return;
  }
  try {
    if (handle.hasPointerCapture?.(pointerId)) {
      handle.releasePointerCapture(pointerId);
    }
  } catch {
    // Capture may already be released by the browser.
  }
};

/**
 * Reusable horizontal resize for docked panels (e.g. right sidebars).
 * Width is kept in page-session memory (no localStorage / sessionStorage).
 *
 * @param {object} options
 * @param {string} [options.cacheKey='default'] - session cache key
 * @param {number|(() => number)} [options.defaultWidth=360]
 * @param {number|(() => number)} [options.minWidth=280]
 * @param {number|(() => number)} [options.maxWidth=720]
 * @param {boolean} [options.enabled=true]
 * @param {'left'|'right'} [options.edge='left'] - which edge is dragged
 */
const useResizablePanelWidth = ({
  cacheKey = "default",
  defaultWidth = 360,
  minWidth = 280,
  maxWidth = 720,
  enabled = true,
  edge = "left",
} = {}) => {
  const clampWidth = useCallback(
    (next) => {
      const min = resolveLimit(minWidth, 280);
      const max = Math.max(min, resolveLimit(maxWidth, 720));
      return Math.min(max, Math.max(min, Math.round(next)));
    },
    [minWidth, maxWidth],
  );

  const [width, setWidth] = useState(() => {
    const cached = sessionWidths.get(cacheKey);
    return clampWidth(
      cached ?? resolveLimit(defaultWidth, 360),
    );
  });
  const [isResizing, setIsResizing] = useState(false);

  const widthRef = useRef(width);
  // Preference vs clamped display width (narrow viewport must not stick).
  const preferredWidthRef = useRef(
    sessionWidths.get(cacheKey) ?? resolveLimit(defaultWidth, 360),
  );
  const dragRef = useRef(null);
  const edgeRef = useRef(edge);
  const clampWidthRef = useRef(clampWidth);
  const cacheKeyRef = useRef(cacheKey);

  // Keep refs current during render so mid-drag handlers never see a stale frame.
  widthRef.current = width;
  edgeRef.current = edge;
  clampWidthRef.current = clampWidth;
  cacheKeyRef.current = cacheKey;

  const tearDownDrag = useCallback((options = {}) => {
    const { persist = true, updateResizingState = true } = options;
    const drag = dragRef.current;
    if (!drag) return false;

    // Clear first so releasePointerCapture → lostpointercapture cannot re-enter.
    dragRef.current = null;

    window.removeEventListener("pointermove", drag.onPointerMove, true);
    window.removeEventListener("pointerup", drag.onPointerUp, true);
    window.removeEventListener("pointercancel", drag.onPointerUp, true);
    if (drag.handle && drag.onLostCapture) {
      drag.handle.removeEventListener(
        "lostpointercapture",
        drag.onLostCapture,
      );
    }

    if (drag.shield?.parentNode) {
      drag.shield.parentNode.removeChild(drag.shield);
    }

    releaseHandleCapture(drag.handle, drag.pointerId);

    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");

    if (persist) {
      preferredWidthRef.current = widthRef.current;
      sessionWidths.set(cacheKeyRef.current, widthRef.current);
    }

    if (updateResizingState) {
      setIsResizing(false);
    }

    return true;
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const applyClamp = () => {
      setWidth(clampWidth(preferredWidthRef.current));
    };

    applyClamp();
    window.addEventListener("resize", applyClamp);
    return () => window.removeEventListener("resize", applyClamp);
  }, [enabled, clampWidth]);

  // If resize is disabled mid-gesture (e.g. breakpoint flip), drop the drag cleanly.
  useEffect(() => {
    if (enabled) return undefined;
    tearDownDrag();
    return undefined;
  }, [enabled, tearDownDrag]);

  // Drop an in-progress drag if the panel unmounts mid-gesture.
  useEffect(
    () => () => {
      tearDownDrag({ updateResizingState: false });
    },
    [tearDownDrag],
  );

  const commitWidth = useCallback(
    (next) => {
      const clamped = clampWidth(next);
      preferredWidthRef.current = clamped;
      sessionWidths.set(cacheKey, clamped);
      setWidth(clamped);
      return clamped;
    },
    [clampWidth, cacheKey],
  );

  const onPointerDown = useCallback(
    (event) => {
      if (!enabled || (event.button != null && event.button !== 0)) return;
      // Ignore a second pointer while a drag is already active.
      if (dragRef.current) return;

      event.preventDefault();
      event.stopPropagation();

      const handle = event.currentTarget;
      const pointerId = event.pointerId;

      const onPointerMove = (moveEvent) => {
        const drag = dragRef.current;
        if (!drag || moveEvent.pointerId !== drag.pointerId) return;

        const delta =
          edgeRef.current === "left"
            ? drag.startX - moveEvent.clientX
            : moveEvent.clientX - drag.startX;
        const next = clampWidthRef.current(drag.startWidth + delta);
        widthRef.current = next;
        preferredWidthRef.current = next;
        setWidth(next);
      };

      const endDrag = () => {
        tearDownDrag();
      };

      const onPointerUp = (upEvent) => {
        const drag = dragRef.current;
        if (!drag || upEvent.pointerId !== drag.pointerId) return;
        endDrag();
      };

      const onLostCapture = (lostEvent) => {
        const drag = dragRef.current;
        if (!drag || lostEvent.pointerId !== drag.pointerId) return;
        endDrag();
      };

      dragRef.current = {
        startX: event.clientX,
        startWidth: widthRef.current,
        pointerId,
        handle,
        // Block EPUB iframes / PDF text layers from eating drag events when
        // the cursor moves left of the handle while expanding the panel.
        shield: createDragShield(),
        onPointerMove,
        onPointerUp,
        onLostCapture,
      };

      // Attach immediately (not in useEffect) so the first moves aren't dropped.
      window.addEventListener("pointermove", onPointerMove, true);
      window.addEventListener("pointerup", onPointerUp, true);
      window.addEventListener("pointercancel", onPointerUp, true);
      // Listen on the handle: lostpointercapture targets the capture element
      // and may not reach window if the node is disconnected.
      handle.addEventListener("lostpointercapture", onLostCapture);

      if (typeof handle.setPointerCapture === "function") {
        try {
          handle.setPointerCapture(pointerId);
        } catch {
          // Older / restricted contexts — shield still covers the common case.
        }
      }

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      setIsResizing(true);
    },
    [enabled, tearDownDrag],
  );

  const onKeyDown = useCallback(
    (event) => {
      if (!enabled) return;

      const growKeys = edge === "left" ? ["ArrowLeft"] : ["ArrowRight"];
      const shrinkKeys = edge === "left" ? ["ArrowRight"] : ["ArrowLeft"];
      const step = event.shiftKey ? KEYBOARD_LARGE_STEP : KEYBOARD_STEP;

      if (growKeys.includes(event.key)) {
        event.preventDefault();
        commitWidth(widthRef.current + step);
      } else if (shrinkKeys.includes(event.key)) {
        event.preventDefault();
        commitWidth(widthRef.current - step);
      } else if (event.key === "Home") {
        event.preventDefault();
        commitWidth(resolveLimit(minWidth, 280));
      } else if (event.key === "End") {
        event.preventDefault();
        commitWidth(resolveLimit(maxWidth, 720));
      }
    },
    [enabled, edge, commitWidth, minWidth, maxWidth],
  );

  const onDoubleClick = useCallback(
    (event) => {
      if (!enabled) return;
      event.preventDefault();
      event.stopPropagation();
      commitWidth(resolveLimit(defaultWidth, 360));
    },
    [enabled, commitWidth, defaultWidth],
  );

  const min = resolveLimit(minWidth, 280);
  const max = Math.max(min, resolveLimit(maxWidth, 720));

  return {
    width,
    isResizing,
    handleProps: {
      role: "separator",
      "aria-orientation": "vertical",
      "aria-valuenow": Math.round(width),
      "aria-valuemin": Math.round(min),
      "aria-valuemax": Math.round(max),
      "aria-disabled": !enabled,
      tabIndex: enabled ? 0 : -1,
      onPointerDown,
      onKeyDown,
      onDoubleClick,
    },
  };
};

export default useResizablePanelWidth;
